/**
 * Preview for societies that are not strong enough for a full indexable page.
 * Shows available fields only; optional Continue opens the (noindex) society route for join/browse.
 */
import React, { useEffect } from 'react';
import LazyImage from '../common/LazyImage';

const SocietyPreviewModal = ({ open, society, onClose, onContinue }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !society) {
    return null;
  }

  const image =
    society.images?.[0]?.original_image ||
    society.images?.[0]?.medium_image ||
    null;
  const description = String(society.description || '').trim();
  const snippet =
    description.length > 280 ? `${description.slice(0, 280).trim()}…` : description;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-gray-200 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="society-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-40 bg-gray-100">
          {image ? (
            <LazyImage
              src={image}
              alt=""
              className="h-full w-full"
              objectFit="cover"
              showProgress={false}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-50 to-slate-100 text-slate-400 text-sm">
              No photo yet
            </div>
          )}
        </div>
        <div className="p-5">
          <h2 id="society-preview-title" className="text-lg font-bold text-gray-900">
            {society.name || 'Society'}
          </h2>
          {society.city ? (
            <p className="text-sm text-gray-500 mt-1">{society.city}</p>
          ) : null}
          {snippet ? (
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">{snippet}</p>
          ) : (
            <p className="text-sm text-gray-500 mt-3">
              Limited information is available for this society yet. You can still open it to join
              or follow when you are ready.
            </p>
          )}
          <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
            <button
              type="button"
              onClick={() => onContinue?.(society)}
              className="w-full sm:flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors min-h-[44px]"
            >
              Open society
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-800 text-sm font-medium hover:bg-gray-50 transition-colors min-h-[44px]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocietyPreviewModal;
